"use client";

import { useEffect } from "react";

/**
 * usePresenterHotkeys — wires the locked single-key + one-modifier-chord
 * keyboard library (Decision 12). Skips firing when focus is in an input,
 * so a PIN field or copy form doesn't intercept stage hotkeys.
 *
 *   single-key:    D   E   I   N   ?   Esc
 *   one-modifier:  Shift+N   Shift+B   Cmd+,
 *
 * Locale-cycling (the historical `L` hotkey) was removed alongside the
 * AR/PT message bundles — the Phase 3 cutover ships English-only.
 *
 * Returns nothing — the handlers fire callbacks the caller supplies.
 */
export type PresenterHotkeys = {
  onPerspectiveExporter?: () => void;   // E
  onPerspectiveImporter?: () => void;   // I
  onCycleLocale?:         () => void;   // L — EN → AR → PT → EN
  onRewindAct?:           () => void;   // Shift+B
  onRewindScreen?:        () => void;   // Shift+N
  onAdvanceScreen?:       () => void;   // N — manual-mode "next screen"
  onToggleChrome?:        () => void;   // ?
  onToggleHelp?:          () => void;   // Cmd+, — surfaces the keyboard help overlay (A2)
  onEscape?:              () => void;   // Esc
  onReopenSignDoc?:       () => void;   // D — re-open the active scene's sign-document modal
};

export function usePresenterHotkeys(handlers: PresenterHotkeys) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        // Esc still escapes a focused input — let the browser handle native blur first
        if (e.key === "Escape") handlers.onEscape?.();
        return;
      }

      const meta = e.metaKey || e.ctrlKey;

      // Cmd+,  → toggle keyboard help overlay
      if (meta && e.key === ",") {
        e.preventDefault();
        handlers.onToggleHelp?.();
        return;
      }
      // Shift+N → rewind to previous screen checkpoint
      // (event.key is the capital "N" when Shift is held — match either)
      if (e.shiftKey && !meta && (e.key === "N" || e.key === "n")) {
        e.preventDefault();
        handlers.onRewindScreen?.();
        return;
      }
      // Shift+B → rewind to most recent act boundary
      if (e.shiftKey && !meta && (e.key === "B" || e.key === "b")) {
        e.preventDefault();
        handlers.onRewindAct?.();
        return;
      }

      // Single-key triggers
      switch (e.key) {
        case "D": case "d":
          e.preventDefault(); handlers.onReopenSignDoc?.(); return;
        case "E": case "e":
          e.preventDefault(); handlers.onPerspectiveExporter?.(); return;
        case "I": case "i":
          e.preventDefault(); handlers.onPerspectiveImporter?.(); return;
        case "L": case "l":
          e.preventDefault(); handlers.onCycleLocale?.(); return;
        case "N": case "n":
          e.preventDefault(); handlers.onAdvanceScreen?.(); return;
        case "?":
          e.preventDefault(); handlers.onToggleChrome?.(); return;
        case "Escape":
          handlers.onEscape?.(); return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlers]);
}
